import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { AuthContextValue, AuthProviderProps } from './AuthContext.types'
import { accessTokenCookieName } from 'config/globalVariables'
import useCookie from 'hooks/useCookie'
import { refreshTokenFn } from 'authorization/refreshToken'
import ConfirmDialog from 'components/ConfirmDialog'
import { Box, darken, IconButton, TextField, ThemeProvider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAlert } from 'context/AlertContext'
import axios from 'axios'
import { checkIcon } from './check-icon'
import { Close } from '@mui/icons-material'
import { themeLight } from 'theme'

const defaultValue: AuthContextValue = {
  token: '',
  updateToken: () => {},
  refreshToken: async () => {},
  isLoading: false,
  changeLoader: () => {},
  get2fa: async (cb: (code: string) => Promise<void>) => {},
}

export const AuthContext = createContext(defaultValue)

export const useAuthContext = (): AuthContextValue => {
  const data = useContext(AuthContext)

  return data
}

const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const { t } = useTranslation()
  const tfaRef = useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [token, updateToken] = useCookie(accessTokenCookieName)
  const [tfa, setTfa] = useState('')
  const [isRequired2fa, setIsRequired2fa] = useState(false)
  const { changeMessage } = useAlert()
  const [callback, setCallback] = useState<((code: string) => void) | undefined>()
  const [onCancel2fa, setOnCancel2fa] = useState<(() => void) | undefined>()

  const refreshToken = async (): Promise<any> => {
    setIsLoading(true)
    const result = await refreshTokenFn(updateToken)
    setIsLoading(false)
    return result
  }

  const changeLoader = (value: boolean): void => {
    setIsLoading(value)
  }

  const clear2fa = (): void => {
    setIsRequired2fa(false)
    setTfa('')
    onCancel2fa?.()
  }

  const get2fa = async (cb: (tfa: string) => void, onCancel?: () => void): Promise<void> => {
    setIsRequired2fa(true)
    setCallback(() => cb)
    setOnCancel2fa(() => onCancel)
  }

  useEffect(() => {
    if (!isRequired2fa && tfa.length === 6 && callback !== undefined) {
      callback(tfa)
    }
  }, [isRequired2fa])

  axios.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config

      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true

        try {
          // Wyślij żądanie do endpointa odświeżania tokena
          const data = await refreshTokenFn(updateToken)
          if (data?.data?.accessToken) {
            // Zapisz nowy token
            // Zaktualizuj nagłówek Authorization
            axios.defaults.headers.common.Authorization = `Bearer ${data.data.accessToken}`

            // Ponów oryginalne żądanie z nowym tokenem
            originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`

            return await axios(originalRequest)
          }
        } catch (refreshError) {
          // Obsłuż błąd odświeżania tokena
        }
      }

      return await Promise.reject(error)
    }
  )

  const handleCode = (): void => {
    const tfaInput = tfaRef.current

    if (tfaInput !== null) {
      const tfa = tfaInput.value

      if (tfa.length === 6) {
        setTfa(tfa)
        setIsRequired2fa(false)
      } else {
        changeMessage(t('dialogs.passTfa'), 'error')
      }
    } else {
      setIsRequired2fa(false)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault()
    handleCode()
  }

  return (
    <AuthContext.Provider value={{ token, updateToken, refreshToken, isLoading, changeLoader, get2fa }}>
      {children}

      <ThemeProvider theme={themeLight}>
        <ConfirmDialog
          onAgree={handleCode}
          onDisagree={clear2fa}
          open={isRequired2fa}
          agreeColor="primary"
          disagreeColor="secondary"
          title={
            <>
              <Box
                flexShrink={0}
                flexGrow={0}
                width="64px"
                height="64px"
                left={-16}
                top={-12}
                position="relative"
                sx={({ palette }) => ({
                  boxShadow: `
                          inset 0 0 90px #DCFAE6, 
                          0 0 0 16px ${palette.background.paper}, 
                          0 0 0 17px ${darken(palette.background.default, 0.05)}, 
                          0 0 0 33px ${palette.background.paper}, 
                          0 0 0 34px ${darken(palette.background.default, 0.03)}, 
                          0 0 0 50px ${palette.background.paper}, 
                          0 0 0 51px ${darken(palette.background.default, 0.01)},
                          0 0 0 67px ${palette.background.paper}, 
                          0 0 0 68px ${palette.background.default}`,
                  border: `8px solid #DCFAE645;`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                {checkIcon}
              </Box>
              <IconButton onClick={clear2fa}>
                <Close />
              </IconButton>
            </>
          }
          question={
            <>
              <Typography fontWeight={600} textAlign="center" variant="h5" mb={1}>
                {t('dialogs.passTfa')}
              </Typography>
              <Typography textAlign="center" mb={2}>
                {t('dialogs.passTfaDescription')}
              </Typography>
              <p></p>
              <form onSubmit={handleSubmit}>
                <TextField placeholder="Code" fullWidth inputRef={tfaRef} />
              </form>
            </>
          }
        />
      </ThemeProvider>
    </AuthContext.Provider>
  )
}

export default AuthProvider
